import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, CardBody, CardHeader, Col, Container, Input, Label, Modal, ModalBody, ModalHeader, Row } from "@/Components/ui";;
import BreadCrumb from "../../Components/Common/BreadCrumb";
import ServerPaginatedTable from "../../Components/Common/ServerPaginatedTable";
import { API_ORDER_TOTAL_CRM, getDataFromUrl, getOrderPgBalance, refundOrderTotal } from "../../api";
import { BACKEND_HOST } from "../../api/client";
import { SUBDOMAIN } from "../../assets/meta/meta";
import moment from "moment";
import Select from "react-select";
import QueryString from "qs";
import CommonDatePicker from "@/Components/Common/CommonDatePicker";
import { dialogService } from "@/utils/dialogService";

const DEFAULT_REFUND_REASON = "관리자취소";

const toNumber = (value) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = (value) => `${toNumber(value).toLocaleString()}원`;

const getRefundableReservations = (record) => (record?.reservations || []).filter((item) => item?.id);

const getRefundableTickets = (record) => (record?.tickets || []).filter((item) => item?.id);

const getStandaloneRefundableOptions = (record) => {
  const reservationIds = new Set(getRefundableReservations(record).map((item) => Number(item.id)));
  return (record?.options || []).filter((item) => {
    if (!item?.id || item.state === "사용함") return false;
    const reservId = Number(item.reserv_id);
    return !reservationIds.has(reservId);
  });
};

const getReservationRefundAmount = (reservation) => toNumber(reservation?.["패키지환불금액"] ?? reservation?.["결제금액"]);

const getOrderRefundAmount = (record) => {
  const itemAmount = [
    ...getRefundableReservations(record).map((item) => toNumber(item?.["환불예상금액"] ?? item?.["패키지환불금액"] ?? item?.["결제금액"])),
    ...getRefundableTickets(record).map((item) => toNumber(item?.paid_amount ?? item?.["가격"])),
    ...getStandaloneRefundableOptions(record).map((item) => toNumber(item?.price)),
  ].reduce((sum, amount) => sum + amount, 0);

  return toNumber(record?.remaining_amount ?? record?.amount ?? itemAmount) || itemAmount;
};

const getOrderRefundableItemCount = (record) => (
  (record?.reservations || []).length
  + (record?.tickets || []).length
  + (record?.options || []).filter((item) => item?.id && item.state !== "사용함").length
  + (record?.shoppingItems || []).filter((item) => item?.state !== "취소완료").length
  + (record?.supplyItems || []).filter((item) => item?.state !== "취소완료").length
  + (record?.memberships || []).filter((item) => item?.state !== "해지").length
);

const FullRefundModal = ({
  open,
  record,
  amount,
  reason,
  error,
  submitting,
  pgBalance,
  pgBalanceLoading,
  pgBalanceError,
  cancelLinkedItems,
  onAmountChange,
  onReasonChange,
  onCancelLinkedItemsChange,
  onClose,
  onSubmit,
}) => {
  const reservations = getRefundableReservations(record).length;
  const tickets = getRefundableTickets(record).length;
  const options = getStandaloneRefundableOptions(record).length;

  return (
    <Modal isOpen={open} onClose={submitting ? undefined : onClose} size="md" centered>
      <ModalHeader toggle={submitting ? undefined : onClose}>전체 환불</ModalHeader>
      <ModalBody>
        <div className="d-flex flex-column gap-3">
          <div>
            <div className="fw-semibold">{record?.order_id ? `주문 #${record.order_id}` : "주문"}</div>
            <div className="text-muted small">
              예약 {reservations}건 · 티켓 {tickets}건 · 옵션 {options}건
            </div>
          </div>
          <div className="border rounded p-3 bg-light">
            {pgBalanceLoading ? (
              <div className="text-muted small">PG 결제금액 조회 중...</div>
            ) : pgBalance ? (
              <div className="d-flex flex-column gap-2">
                <div className="d-flex justify-content-between gap-3">
                  <span className="text-muted">PG 결제금액</span>
                  <strong>{formatCurrency(pgBalance.payment_amount)}</strong>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <span className="text-muted">이미 취소된 금액</span>
                  <strong>{formatCurrency(pgBalance.canceled_amount)}</strong>
                </div>
                <div className="d-flex justify-content-between gap-3">
                  <span className="text-muted">남은 금액</span>
                  <strong>{formatCurrency(pgBalance.balance_amount)}</strong>
                </div>
                <div className="text-muted small">
                  {pgBalance.paygate ? pgBalance.paygate.toUpperCase() : "PG"} {pgBalance.status || ""}
                </div>
              </div>
            ) : (
              <div className="text-danger small">{pgBalanceError || "PG 결제금액을 조회하지 못했습니다."}</div>
            )}
          </div>
          <div className="text-danger text-md fw-semibold d-flex flex-column gap-1">
            <div>PG에서 즉시 취소되며 처리 후 되돌릴 수 없습니다.</div>
          </div>
          <div className="border rounded p-3">
            <label htmlFor="full-refund-cancel-linked-items" className="d-flex align-items-center gap-2 mb-1 fw-semibold">
              <Input
                id="full-refund-cancel-linked-items"
                type="checkbox"
                checked={cancelLinkedItems}
                onChange={(event) => onCancelLinkedItemsChange(event.target.checked)}
                disabled={submitting}
              />
              <span>연결된 주문 항목도 함께 취소</span>
            </label>
            <div className="text-muted small">
              {cancelLinkedItems
                ? "체크하면 PG 환불과 동시에 연결된 예약, 티켓, 옵션 등 주문 항목도 취소 처리됩니다."
                : "체크 해제 시 예약, 티켓, 옵션 등 주문 항목은 그대로 두고 PG 환불만 요청합니다."}
            </div>
          </div>
          <div>
            <Label htmlFor="full-refund-amount" className="form-label">환불 금액</Label>
            <Input
              id="full-refund-amount"
              type="number"
              min="0"
              value={amount}
              onChange={(event) => onAmountChange(event.target.value)}
              disabled={submitting}
            />
          </div>
          <div>
            <Label htmlFor="full-refund-reason" className="form-label">환불 사유</Label>
            <Input
              id="full-refund-reason"
              value={reason}
              maxLength={10}
              onChange={(event) => onReasonChange(event.target.value)}
              disabled={submitting}
              placeholder="관리자취소"
            />
            {error ? <div className="text-danger small mt-1">{error}</div> : null}
          </div>
          <div className="d-flex justify-content-end gap-2">
            <Button color="light" onClick={onClose} disabled={submitting}>닫기</Button>
            <Button color="danger" onClick={onSubmit} disabled={submitting || pgBalanceLoading || toNumber(amount) <= 0}>
              {submitting ? "처리 중..." : "환불 진행"}
            </Button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

const VisibleDetailSection = ({ title, count, children }) => {
  if (!count) return null;

  return (
    <details open>
      <summary className="fw-semibold">{title} ({count})</summary>
      <div className="mt-2">{children}</div>
    </details>
  );
};

const DetailModal = ({ open, onClose, record }) => {
  const receiptSupportedPaygates = ["toss", "nicepay", "hecto"];
  const pick = (obj, fields, fallback = "-") => {
    if (!obj) return fallback;
    for (const f of fields) {
      const v = obj[f];
      if (v !== undefined && v !== null && v !== "") return v;
    }
    return fallback;
  };
  const canOpenReceipt = !!record?.order_id && receiptSupportedPaygates.includes(record?.paygate);
  const handleOpenReceipt = () => {
    if (!canOpenReceipt) return;
    window.open(`${BACKEND_HOST}/package/get-order-receipt/${encodeURIComponent(record.order_id)}/`, "_blank", "noopener,noreferrer");
  };
  const reservations = record?.reservations || [];
  const reservationCancels = record?.reservationCancels || [];
  const tickets = record?.tickets || [];
  const ticketCancels = record?.ticketCancels || [];
  const options = record?.options || [];
  const optionCancels = record?.optionCancels || [];
  const shoppingItems = record?.shoppingItems || [];
  const supplyItems = record?.supplyItems || [];
  const memberships = record?.memberships || [];

  return (
    <Modal isOpen={open} onClose={onClose} size="lg" centered>
      <ModalHeader toggle={onClose}>주문 상세 #{record?.order_id}</ModalHeader>
      <ModalBody>
        {record ? (
          <div className="d-flex flex-column gap-3">
            <div>
              <div className="fw-semibold">기본정보</div>
              <div className="text-muted small">주문일: {moment(record.order_date).format("YYYY-MM-DD HH:mm")}</div>
              <div className="text-muted small">상태: {record.state}</div>
              {/* <div className="text-muted small">결제수단: {record.pay_method}</div> */}
              <div className="text-muted small">결제유형: {record.payment_type}</div>
              <div className="text-muted small">PG: {record.paygate}{record.pg_mid ? ` (${record.pg_mid})` : ""}</div>
              <div className="text-muted small">결제금액: {record.amount?.toLocaleString?.() || record.amount}</div>
              {canOpenReceipt ? (
                <div className="mt-2">
                  <Button size="sm" color="secondary" onClick={handleOpenReceipt}>PG 영수증 보기</Button>
                </div>
              ) : null}
            </div>
            <VisibleDetailSection title="예약" count={reservations.length}>
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>상품</th>
                    <th>자리</th>
                    <th>상태</th>
                    <th>시작</th>
                    <th>종료</th>
                  </tr>
                </thead>
                <tbody>
                  {reservations.map((r) => {
                    const product = pick(r, ["예약상품", "package_name", "package"]);
                    const spot = pick(r, ["자리", "spot_name", "spot"]);
                    const state = pick(r, ["예약상태", "state"], "-");
                    const start = pick(r, ["예약시작일", "start_date"], null);
                    const end = pick(r, ["예약종료일", "end_date"], null);
                    return (
                      <tr key={`r-${r.id}`}>
                        <td>{r.id}</td>
                        <td>{product}</td>
                        <td>{spot}</td>
                        <td>{state}</td>
                        <td>{start ? moment(start).format("YYYY-MM-DD") : "-"}</td>
                        <td>{end ? moment(end).format("YYYY-MM-DD") : "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </VisibleDetailSection>
            <VisibleDetailSection title="예약 취소" count={reservationCancels.length}>
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>상품</th>
                    <th>자리</th>
                    <th>취소일</th>
                    <th>금액</th>
                  </tr>
                </thead>
                <tbody>
                  {reservationCancels.map((c) => {
                    const product = pick(c, ["예약상품", "package_name", "package"]);
                    const spot = pick(c, ["자리", "spot_name", "spot"]);
                    const cancelDate = pick(c, ["취소일", "cancel_date"], null);
                    const amount = pick(c, ["취소금액", "canceled_amount", "amount"], 0);
                    return (
                      <tr key={`rc-${c.id}`}>
                        <td>{c.id}</td>
                        <td>{product}</td>
                        <td>{spot}</td>
                        <td>{cancelDate ? moment(cancelDate).format("YYYY-MM-DD HH:mm") : "-"}</td>
                        <td>{amount?.toLocaleString?.() || amount}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </VisibleDetailSection>
            <VisibleDetailSection title="티켓" count={tickets.length}>
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>상품</th>
                    <th>상태</th>
                    <th>만료</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((t) => {
                    const product = pick(t, ["상품", "product_name", "ticket_product_name"], "-");
                    const state = pick(t, ["상태", "state"], "-");
                    const exp = pick(t, ["만료일", "expiration_date"], null);
                    return (
                      <tr key={`t-${t.id}`}>
                        <td>{t.id}</td>
                        <td>{product}</td>
                        <td>{state}</td>
                        <td>{exp ? moment(exp).format("YYYY-MM-DD") : "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </VisibleDetailSection>
            <VisibleDetailSection title="티켓 취소" count={ticketCancels.length}>
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>상품</th>
                    <th>취소일</th>
                  </tr>
                </thead>
                <tbody>
                  {ticketCancels.map((tc) => {
                    const product = pick(tc, ["상품", "product_name", "ticket_product_name"], "-");
                    const cancelDate = pick(tc, ["취소일", "cancel_date"], null);
                    return (
                      <tr key={`tc-${tc.id}`}>
                        <td>{tc.id}</td>
                        <td>{product}</td>
                        <td>{cancelDate ? moment(cancelDate).format("YYYY-MM-DD HH:mm") : "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </VisibleDetailSection>
            <VisibleDetailSection title="옵션" count={options.length}>
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>옵션</th>
                    <th>수량</th>
                    <th>금액</th>
                  </tr>
                </thead>
                <tbody>
                  {options.map((o) => {
                    const name = pick(o, ["name", "option_name"], "-");
                    const qty = o.qty || 1;
                    const price = pick(o, ["price"], 0);
                    return (
                      <tr key={`o-${o.id}`}>
                        <td>{o.id}</td>
                        <td>{name}</td>
                        <td>{qty}</td>
                        <td>{price?.toLocaleString?.() || price}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </VisibleDetailSection>
            <VisibleDetailSection title="옵션 취소" count={optionCancels.length}>
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>옵션</th>
                    <th>취소일</th>
                    <th>금액</th>
                  </tr>
                </thead>
                <tbody>
                  {optionCancels.map((oc) => {
                    const name = pick(oc, ["name", "option_name"], "-");
                    const cancelDate = pick(oc, ["취소일", "cancel_date"], null);
                    const amount = pick(oc, ["canceled_amount", "price", "amount"], 0);
                    return (
                      <tr key={`oc-${oc.id}`}>
                        <td>{oc.id}</td>
                        <td>{name}</td>
                        <td>{cancelDate ? moment(cancelDate).format("YYYY-MM-DD HH:mm") : "-"}</td>
                        <td>{amount?.toLocaleString?.() || amount}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </VisibleDetailSection>
            <VisibleDetailSection title="쇼핑" count={shoppingItems.length}>
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>상품</th>
                    <th>수량</th>
                    <th>상태</th>
                    <th>금액</th>
                  </tr>
                </thead>
                <tbody>
                  {shoppingItems.map((item) => (
                    <tr key={`shopping-${item.id}`}>
                      <td>{item.id}</td>
                      <td>{pick(item, ["name", "상품"], "-")}</td>
                      <td>{item.quantity ?? 1}</td>
                      <td>{pick(item, ["state", "상태"], "-")}</td>
                      <td>{toNumber(item.paid_amount).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </VisibleDetailSection>
            <VisibleDetailSection title="서플라이" count={supplyItems.length}>
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>상품</th>
                    <th>수량</th>
                    <th>상태</th>
                    <th>금액</th>
                  </tr>
                </thead>
                <tbody>
                  {supplyItems.map((item) => (
                    <tr key={`supply-${item.id}`}>
                      <td>{item.id}</td>
                      <td>{pick(item, ["name", "상품"], "-")}</td>
                      <td>{item.quantity ?? 1}</td>
                      <td>{pick(item, ["state", "상태"], "-")}</td>
                      <td>{toNumber(item.paid_amount).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </VisibleDetailSection>
            <VisibleDetailSection title="멤버십" count={memberships.length}>
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>상품</th>
                    <th>상태</th>
                    <th>기간</th>
                    <th>금액</th>
                  </tr>
                </thead>
                <tbody>
                  {memberships.map((item) => (
                    <tr key={`membership-${item.id}`}>
                      <td>{item.id}</td>
                      <td>{pick(item.product, ["name"], item.name || "-")}</td>
                      <td>{item.state || "-"}</td>
                      <td>{item.start_date && item.end_date ? `${item.start_date} ~ ${item.end_date}` : "-"}</td>
                      <td>{toNumber(item.paid_amount).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </VisibleDetailSection>
          </div>
        ) : null}
      </ModalBody>
    </Modal>
  );
};

const UserOrderTotalList = ({ fixedFilterPhone }) => {
  const columns = useMemo(
    () => [
      {
        Header: "주문일",
        accessor: "order_date",
        id: "order_date",
        Cell: (cellProps) => (
          <div>
            <div>{moment(cellProps.row.original.order_date).format("YYYY-MM-DD HH:mm")}</div>
            <div className="text-muted">#{cellProps.row.original.order_id}</div>
          </div>
        ),
      },
      {
        Header: "상태",
        accessor: "state",
        id: "state",
      },
      //   {
      //     Header: "결제수단",
      //     accessor: "pay_method",
      //     id: "pay_method",
      //   },
      {
        Header: "금액",
        accessor: "amount",
        id: "amount",
        Cell: (cellProps) => {
          const remainingAmount = cellProps.row.original.remaining_amount;
          const originalAmount = cellProps.row.original.amount;
          return (
            <div>
              <div>{(remainingAmount ?? originalAmount ?? 0).toLocaleString?.() || remainingAmount || originalAmount}</div>
              {remainingAmount !== undefined && remainingAmount !== originalAmount ? (
                <div className="text-muted small">원결제 {(originalAmount ?? 0).toLocaleString?.() || originalAmount}</div>
              ) : null}
            </div>
          );
        },
      },
      {
        Header: "PG",
        accessor: "paygate",
        id: "paygate",
        Cell: (cellProps) => <span>{cellProps.row.original.pg_mid ? `${cellProps.row.original.paygate} (${cellProps.row.original.pg_mid})` : cellProps.row.original.paygate}</span>,
      },
      {
        Header: "Actions",
        accessor: "Actions",
        id: "Actions",
        Cell: (cellProps) => (
          <div className="d-flex justify-content-end gap-2">
            <Button size="sm" color="secondary" onClick={() => { setTarget(cellProps.row.original); setOpen(true); }}>상세</Button>
	            <Button
	              size="sm"
	              color="danger"
	              onClick={() => openRefundModal(cellProps.row.original)}
	              disabled={getOrderRefundAmount(cellProps.row.original) <= 0}
	            >
              전체 환불
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  const [data, setData] = useState([]);
  const [previous, setPrevious] = useState("");
  const [next, setNext] = useState("");
  const [count, setCount] = useState(0);
  const site = (SUBDOMAIN || "").replace("crm", "");
  const initialPhone = (fixedFilterPhone || "").toString();
  const [query, setQuery] = useState({ filter_site: site, ...(initialPhone ? { filter_phone: initialPhone } : {}) });
  const [orderId, setOrderId] = useState("");
  const [filterStates, setFilterStates] = useState([]); // array of values
  const [filterDate, setFilterDate] = useState("");
  const [filterPhone, setFilterPhone] = useState(initialPhone);

  const stateOptions = [
    { label: "결제완료", value: "결제완료" },
    { label: "부분취소", value: "부분취소" },
    { label: "취소완료", value: "취소완료" },
    { label: "예약완료(기존)", value: "예약완료" },
  ];

  const onRequestData = (url) => {
    getDataFromUrl(url)
      .then((res) => {
        // Backend returns a non-DRF style object; normalize to table format
        const results = res.results || [];
        setData(results);
        // Build prev/next links from pagination numbers when available
        const page = res.page || 1;
        const pageSize = res.page_size || 15;
        const total = res.total || results.length;
        setCount(total);
        const totalPage = res.total_page || Math.ceil(total / pageSize);
        const base = API_ORDER_TOTAL_CRM.replace(/\/$/, "");
        const shared = { ...query };
        const qPrev = page > 1 ? `${base}/?${QueryString.stringify({ ...shared, page: page - 1, page_size: pageSize })}` : "";
        const qNext = page < totalPage ? `${base}/?${QueryString.stringify({ ...shared, page: page + 1, page_size: pageSize })}` : "";
        setPrevious(qPrev);
        setNext(qNext);
      })
      .catch(console.warn);
  };

  const refetch = () => {
    const base = API_ORDER_TOTAL_CRM.replace(/\/$/, "");
    const q = QueryString.stringify({ ...query, page: 1 });
    onRequestData(`${base}/?${q}`);
  };

  useEffect(() => {
    if (!previous && !next && data.length === 0) {
      refetch();
    }
  }, []);

  // Keep local phone state/query in sync when fixed phone prop changes
  useEffect(() => {
    const nextPhone = (fixedFilterPhone || "").toString();
    setFilterPhone(nextPhone);
    setQuery((prev) => {
      const updated = { ...prev };
      if (nextPhone) updated.filter_phone = nextPhone; else delete updated.filter_phone;
      return updated;
    });
  }, [fixedFilterPhone]);

  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundAmount, setRefundAmount] = useState("0");
  const [refundReason, setRefundReason] = useState(DEFAULT_REFUND_REASON);
  const [refundError, setRefundError] = useState("");
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [refundPgBalance, setRefundPgBalance] = useState(null);
  const [refundPgBalanceLoading, setRefundPgBalanceLoading] = useState(false);
  const [refundPgBalanceError, setRefundPgBalanceError] = useState("");
  const [refundCancelLinkedItems, setRefundCancelLinkedItems] = useState(true);

  const openRefundModal = (record) => {
    setRefundTarget(record);
    setRefundAmount(String(getOrderRefundAmount(record)));
    setRefundReason(DEFAULT_REFUND_REASON);
    setRefundError("");
    setRefundPgBalance(null);
    setRefundPgBalanceError("");
    setRefundPgBalanceLoading(true);
    setRefundCancelLinkedItems(true);
    setRefundOpen(true);

    getOrderPgBalance(record.id, site)
      .then((data) => {
        setRefundPgBalance(data);
        if (toNumber(data?.balance_amount) > 0) {
          setRefundAmount(String(toNumber(data.balance_amount)));
        }
      })
      .catch((error) => {
        setRefundPgBalance(null);
        setRefundPgBalanceError(error?.response?.data?.error || error?.message || "PG 결제금액을 조회하지 못했습니다.");
      })
      .finally(() => {
        setRefundPgBalanceLoading(false);
      });
  };

  const closeRefundModal = () => {
    if (refundSubmitting) return;
    setRefundOpen(false);
    setRefundTarget(null);
    setRefundAmount("0");
    setRefundReason(DEFAULT_REFUND_REASON);
    setRefundError("");
    setRefundPgBalance(null);
    setRefundPgBalanceLoading(false);
    setRefundPgBalanceError("");
    setRefundCancelLinkedItems(true);
  };

  const handleFullRefund = async () => {
    if (!refundTarget) return;
    const requestedRefundAmount = toNumber(refundAmount);
    const reason = refundReason.trim();
    if (requestedRefundAmount <= 0) {
      setRefundError("환불 금액을 입력해주세요.");
      return;
    }
    if (!reason) {
      setRefundError("환불 사유를 입력해주세요.");
      return;
    }

    if (getOrderRefundableItemCount(refundTarget) === 0) {
      setRefundError("환불할 항목이 없습니다.");
      return;
    }

    setRefundSubmitting(true);
    setRefundError("");
    try {
      await refundOrderTotal(refundTarget.id, requestedRefundAmount, reason, site, refundCancelLinkedItems);
      await dialogService.handleAlert("전체 환불 요청이 완료되었습니다.");
      setRefundOpen(false);
      setRefundTarget(null);
      setRefundAmount("0");
      setRefundReason(DEFAULT_REFUND_REASON);
      setRefundError("");
      setRefundPgBalance(null);
      setRefundPgBalanceLoading(false);
      setRefundPgBalanceError("");
      setRefundCancelLinkedItems(true);
      refetch();
    } catch (error) {
      await dialogService.handleAlert(error, { force: "error" });
    } finally {
      setRefundSubmitting(false);
    }
  };

  const contents = (<>
    <div className="mb-3">
      <Row className="align-items-end g-3">
        <Col md={3}>
          <Label className="form-label">전화번호</Label>
          <Input
            placeholder="01012345678"
            value={filterPhone}
            onChange={(e) => setFilterPhone(e.target.value)}
            disabled={!!fixedFilterPhone}
          />
        </Col>
        <Col md={3}>
          <Label className="form-label">주문번호</Label>
          <Input
            placeholder="order_id"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Label className="form-label">상태</Label>
          <Select
            isMulti
            value={stateOptions.filter(o => filterStates.includes(o.value))}
            options={stateOptions}
            classNamePrefix="select2"
            onChange={(vals) => setFilterStates((vals || []).map(v => v.value))}
          />
        </Col>
        <Col md={3}>
          <Label className="form-label">주문일자</Label>
          <CommonDatePicker value={filterDate} clearable onChangeValue={setFilterDate} />
        </Col>
        <Col md={12} className="d-flex gap-2">
          <Button color="primary" onClick={() => {
            const params = {
              ...query,
              filter_site: site,
            };
            if (orderId) params.order_id = orderId;
            if (filterStates.length) params.filter_states = filterStates.join(",");
            if (filterDate) params.filter_date = filterDate;
            if (filterPhone) params.filter_phone = filterPhone;
            // Ensure fixed phone cannot be removed
            if (fixedFilterPhone) params.filter_phone = fixedFilterPhone.toString();
            setQuery(params);
            const base = API_ORDER_TOTAL_CRM.replace(/\/$/, "");
            const q = QueryString.stringify({ ...params, page: 1 });
            onRequestData(`${base}/?${q}`);
          }}>검색</Button>
          <Button color="secondary" onClick={() => {
            setOrderId("");
            setFilterStates([]);
            setFilterDate("");
            setFilterPhone(fixedFilterPhone ? fixedFilterPhone.toString() : "");
            const params = { filter_site: site, ...(fixedFilterPhone ? { filter_phone: fixedFilterPhone.toString() } : {}) };
            setQuery(params);
            const base = API_ORDER_TOTAL_CRM.replace(/\/$/, "");
            const q = QueryString.stringify({ ...params, page: 1 });
            onRequestData(`${base}/?${q}`);
          }}>초기화</Button>
        </Col>
      </Row>
    </div>
    <ServerPaginatedTable
      id="orders-table"
      url={API_ORDER_TOTAL_CRM}
      columns={columns}
      data={data}
      previous={previous}
      next={next}
      count={count}
      onRequestData={(u) => {
        // merge current table query with our filters
        // u already includes query params; just delegate
        onRequestData(u);
      }}
      onChangeQuery={(param) => {
        // preserve our existing filter fields when table updates sorting/paging/search
        const merged = { ...query, ...param, filter_site: site };
        if (fixedFilterPhone) merged.filter_phone = fixedFilterPhone.toString();
        setQuery(merged);
      }}
      tableQuery={{ ...query }}
      categories={[]}
      serachable={false}
    />
  </>)

  if (fixedFilterPhone) {
    return (
      <div className="page-content">
        {contents}
        <DetailModal open={open} onClose={() => setOpen(false)} record={target} />
	        <FullRefundModal
	          open={refundOpen}
	          record={refundTarget}
	          amount={refundAmount}
	          reason={refundReason}
	          error={refundError}
	          submitting={refundSubmitting}
	          pgBalance={refundPgBalance}
	          pgBalanceLoading={refundPgBalanceLoading}
	          pgBalanceError={refundPgBalanceError}
	          cancelLinkedItems={refundCancelLinkedItems}
	          onAmountChange={(value) => {
	            setRefundAmount(value);
	            if (refundError) setRefundError("");
	          }}
	          onReasonChange={(value) => {
	            setRefundReason(value);
	            if (refundError) setRefundError("");
          }}
          onCancelLinkedItemsChange={setRefundCancelLinkedItems}
          onClose={closeRefundModal}
          onSubmit={handleFullRefund}
        />
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="주문내역" pageTitle="관리" />
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="card-header border-0">
                <div className="d-flex align-items-center">
                  <h5 className="card-title mb-0 grow">주문 내역</h5>
                </div>
              </CardHeader>
              <CardBody className="pt-0">
                {contents}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <DetailModal open={open} onClose={() => setOpen(false)} record={target} />
	      <FullRefundModal
	        open={refundOpen}
	        record={refundTarget}
	        amount={refundAmount}
	        reason={refundReason}
	        error={refundError}
	        submitting={refundSubmitting}
	        pgBalance={refundPgBalance}
	        pgBalanceLoading={refundPgBalanceLoading}
	        pgBalanceError={refundPgBalanceError}
	        cancelLinkedItems={refundCancelLinkedItems}
	        onAmountChange={(value) => {
	          setRefundAmount(value);
	          if (refundError) setRefundError("");
	        }}
	        onReasonChange={(value) => {
	          setRefundReason(value);
	          if (refundError) setRefundError("");
        }}
        onCancelLinkedItemsChange={setRefundCancelLinkedItems}
        onClose={closeRefundModal}
        onSubmit={handleFullRefund}
      />
    </div>
  );
};

export default UserOrderTotalList;
